import { nullSafeToString, intToFormattedString, toDisplayPrice } from '../lib/util';

/*
 * A stock line carries one of these two variants, never both: an item bought from a site's own offering
 * has the site variant, one bought from a shared system offering has the system variant. The API omits
 * whichever does not apply, so the model resolves the pair to a single `variant` and leaves consumers
 * out of a choice the server has already made.
 */

function toVariant (data) {
    const variant = data.stockSiteOfferingVariant || data.stockSystemOfferingVariant;

    if (!variant) {
        return null;
    }

    /*
     * attribute1/2/3Choice are deliberately not mapped, though the payload carries them. They are the
     * same three values OrderedItem.stockVariantDetails is built from - server side, by
     * RegularOrderItemDTOMapperImpl#extractStockVariantDetails, resolving the site/system variant pair
     * the same way this does - so exposing them here would give a consumer two places to read one thing
     * and a decision to make about which. Leave them on OrderedItem until something needs them as
     * discrete values.
     */

    return Object.freeze({
        id: variant.id,
        sku: nullSafeToString(variant.skuSuffix),
        price: variant.price || 0,
        displayPrice: toDisplayPrice(variant.price),
        availableInventory: variant.availableInventory || 0,
        displayAvailableInventory: intToFormattedString(variant.availableInventory || 0),
    });
}

/**
 * A stock (warehoused, non-printed) line on a submitted {@link Order Order}.
 *
 * This is not a replacement for the {@link OrderedItem OrderedItem} of the same line, and not something
 * to render beside one - the same stock line is described by both. `OrderedItem` is the line: what was
 * ordered, what it cost, where it shipped. This is the *variant* behind it, resolved and current. They
 * share an id, which is how to join them.
 *
 * The shape is deliberately thin because nearly everything about a stock line is already on the
 * `OrderedItem`: a stock line has no production, proofing or artwork state, and its name, quantity,
 * price, backorder quantity and variant summary all live there. Two things do not, and they are the
 * reason {@link Collaterate.Order.getStockItems getStockItems} exists:
 *
 * - **`variant.availableInventory`** - live warehouse stock, read at call time rather than recorded at
 *   order time. Nothing else in this SDK exposes inventory, so a stock level or a sold-out reorder
 *   button has to come from here. `variant.price` is the variant's price today for the same reason.
 * - **A stock line inside a product set**, which `getItems` hides in favour of the set's primary line.
 *
 * Which variant the shopper chose - the size, the colour - is on the `OrderedItem` as
 * `stockVariantDetails` and is not repeated here.
 *
 * @property {number} id - (Read only) This item's unique id. The same id space as an {@link OrderedItem OrderedItem}.
 * @property {string} name - (Read only) The item's name.
 * @property {?OrderedStockItem~variant} variant - (Read only) The stock variant that was ordered, resolved from whichever of the site or system offering this item came from. Null if the item carries neither.
 */

class OrderedStockItem {
    constructor (data = {}) {
        this.id = data.id;
        this.name = nullSafeToString(data.name);
        this.variant = toVariant(data);

        Object.freeze(this);
    }
}

export default OrderedStockItem;

/**
 * The stock variant an {@link OrderedStockItem OrderedStockItem} was ordered as.
 *
 * @typedef {Object} OrderedStockItem~variant
 * @property {number} id - The variant's unique id.
 * @property {string} sku - The variant's SKU suffix. Empty when the variant has none.
 * @property {number} price - The variant's current price.
 * @property {string} displayPrice - price formatted with commas and two decimal places.
 * @property {number} availableInventory - How many are in stock right now. This is live inventory, not what was ordered.
 * @property {string} displayAvailableInventory - availableInventory as a string, formatted with commas.
 */
